import React, { useEffect, useRef, useState } from "react";
import { api, API } from "@/lib/api";
import { Send, Loader2, User, Bot } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { SourceBadge, Disclaimer } from "@/components/Badges";
import { MicButton } from "@/components/MicButton";
import { SpeakerButton } from "@/components/SpeakerButton";
import { SensitiveInfoWarning } from "@/components/SensitiveInfoWarning";
import { useLang } from "@/context/LanguageContext";

const SUGGESTIONS_BY_LANG = {
  en: [
    "What is a summons?",
    "What is the difference between a plaintiff and a defendant?",
    "What happens at a status hearing?",
    "How do I ask for a continuance?",
    "What should I bring to court?",
  ],
  es: [
    "¿Qué es una citación (summons)?",
    "¿Cuál es la diferencia entre demandante y demandado?",
    "¿Qué pasa en una audiencia de estado?",
    "¿Cómo pido un aplazamiento?",
    "¿Qué debo llevar a la corte?",
  ],
};

const makeId = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID)
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

export default function Deborah() {
  const { t, lang } = useLang();
  const [sessionId, setSessionId] = useState(() => localStorage.getItem("deborah_session") || null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);
  const abortRef = useRef(null);

  useEffect(() => {
    if (sessionId) localStorage.setItem("deborah_session", sessionId);
  }, [sessionId]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 99999, behavior: "smooth" });
  }, [messages, loading]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    setInput("");
    setMessages((m) => [...m, { id: makeId(), role: "user", text: msg }]);
    const aiId = makeId();
    setMessages((m) => [...m, { id: aiId, role: "deborah", text: "", streaming: true }]);
    setLoading(true);

    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const resp = await fetch(`${API}/deborah/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        signal: controller.signal,
        body: JSON.stringify({
          message: msg,
          session_id: sessionId,
          jurisdiction_state: "IL",
          language: lang,
        }),
      });
      if (!resp.ok || !resp.body) throw new Error(`stream ${resp.status}`);

      const reader = resp.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let buffer = "";
      let finalMeta = null;

      const applyChunk = (piece) => {
        setMessages((all) => all.map((m) => (m.id === aiId ? { ...m, text: (m.text || "") + piece } : m)));
      };

      // Parse SSE frames: split on \n\n
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let idx;
        while ((idx = buffer.indexOf("\n\n")) !== -1) {
          const frame = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 2);
          const lines = frame.split("\n");
          let event = "message";
          const dataLines = [];
          for (const line of lines) {
            if (line.startsWith("event:")) event = line.slice(6).trim();
            else if (line.startsWith("data:")) dataLines.push(line.slice(5).replace(/^ /, ""));
          }
          const payload = dataLines.join("\n");
          if (event === "message") {
            if (payload) applyChunk(payload);
          } else if (event === "done") {
            try { finalMeta = JSON.parse(payload); } catch (e) { console.warn("done parse", e); }
          } else if (event === "error") {
            throw new Error(payload || "stream error");
          }
        }
      }

      if (finalMeta) {
        if (finalMeta.session_id && !sessionId) setSessionId(finalMeta.session_id);
        setMessages((all) => all.map((m) => (
          m.id === aiId
            ? {
                ...m,
                streaming: false,
                source: finalMeta.source_label || "GENERAL_EDUCATIONAL",
                disclaimer: lang === "es"
                  ? "Esta es información educativa, no asesoría legal."
                  : "This is educational information, not legal advice. For advice about your specific situation, contact a licensed attorney or legal-aid organization.",
              }
            : m
        )));
      } else {
        setMessages((all) => all.map((m) => (m.id === aiId ? { ...m, streaming: false } : m)));
      }
    } catch (e) {
      if (e.name !== "AbortError") console.error("Deborah stream error", e);
      // Fallback to non-streaming call so the user still gets an answer
      try {
        const { data } = await api.post("/deborah/chat", {
          message: msg, session_id: sessionId, jurisdiction_state: "IL", language: lang,
        });
        if (!sessionId) setSessionId(data.session_id);
        setMessages((all) => all.map((m) => (
          m.id === aiId
            ? { ...m, text: data.text, streaming: false, source: data.source_label, disclaimer: data.disclaimer }
            : m
        )));
      } catch (err) {
        console.error("Deborah fallback failed", err);
        setMessages((all) => all.map((m) => (
          m.id === aiId
            ? { ...m, text: "I couldn't reach my knowledge base just now. Please try again in a moment.", streaming: false, source: "GENERAL_EDUCATIONAL" }
            : m
        )));
      }
    } finally {
      abortRef.current = null;
      setLoading(false);
    }
  };

  const suggestions = SUGGESTIONS_BY_LANG[lang] || SUGGESTIONS_BY_LANG.en;

  return (
    <div className="max-w-3xl mx-auto flex flex-col h-[calc(100vh-14rem)]">
      <div className="mb-4">
        <h1 className="text-2xl sm:text-3xl font-bold">{t("deborah.title")}</h1>
        <p className="text-[#4A525A]">{t("deborah.subtitle")}</p>
        <div className="mt-2"><SensitiveInfoWarning testid="deborah-sensitive-warning" /></div>
      </div>

      <div
        ref={scrollRef}
        data-testid="deborah-messages"
        className="flex-1 overflow-y-auto space-y-4 bg-white border-2 border-[#E4DDD3] rounded-2xl p-4 sm:p-6"
      >
        {messages.length === 0 && (
          <div className="text-center py-6">
            <div className="w-14 h-14 mx-auto rounded-2xl bg-[#FAF5EC] flex items-center justify-center mb-3">
              <Bot className="w-7 h-7 text-[#C85A32]" />
            </div>
            <p className="text-[#4A525A]">{t("deborah.suggest_prompt")}</p>
            <div className="flex flex-wrap gap-2 justify-center mt-4">
              {suggestions.map((s, idx) => (
                <button
                  key={s}
                  data-testid={`deborah-suggestion-${idx}`}
                  onClick={() => send(s)}
                  className="pod-focus text-sm bg-[#FAF5EC] hover:bg-[#F3EFE6] border border-[#E4DDD3] text-[#1C2024] px-3 py-1.5 rounded-full"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m) => (
          <div key={m.id} className={`flex gap-3 ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            {m.role === "deborah" && (
              <div className="w-9 h-9 shrink-0 rounded-full bg-[#FAF5EC] flex items-center justify-center mt-1">
                <Bot className="w-5 h-5 text-[#C85A32]" />
              </div>
            )}
            <div
              className={
                m.role === "user"
                  ? "bg-[#C85A32] text-white rounded-2xl rounded-tr-none p-4 max-w-[85%] leading-relaxed shadow-sm"
                  : "bg-[#FBF9F5] border-2 border-[#E4DDD3] rounded-2xl rounded-tl-none p-4 max-w-[85%] leading-relaxed"
              }
              data-testid={m.role === "user" ? `msg-user-${m.id}` : `msg-deborah-${m.id}`}
            >
              {m.role === "deborah" && m.source && (
                <div className="mb-2 flex items-center gap-2 flex-wrap">
                  <SourceBadge label={m.source} />
                  {!m.streaming && m.text && (
                    <SpeakerButton text={m.text} lang={lang} testid={`speaker-${m.id}`} />
                  )}
                </div>
              )}
              {m.role === "deborah" ? (
                <div className="deborah-md">
                  <ReactMarkdown
                    components={{
                      h1: ({ children }) => <h3 className="font-bold text-lg mt-2 mb-1">{children}</h3>,
                      h2: ({ children }) => <h3 className="font-bold text-base mt-2 mb-1">{children}</h3>,
                      h3: ({ children }) => <h4 className="font-bold text-base mt-2 mb-1">{children}</h4>,
                      strong: ({ children }) => <strong className="font-bold">{children}</strong>,
                      em: ({ children }) => <em className="italic">{children}</em>,
                      ul: ({ children }) => <ul className="list-disc pl-5 my-2 space-y-1">{children}</ul>,
                      ol: ({ children }) => <ol className="list-decimal pl-5 my-2 space-y-1">{children}</ol>,
                      p: ({ children }) => <p className="my-1.5 leading-relaxed">{children}</p>,
                      hr: () => <hr className="my-2 border-[#E4DDD3]" />,
                      code: ({ children }) => <code className="mono bg-[#F3EFE6] px-1 rounded">{children}</code>,
                    }}
                  >
                    {m.text || ""}
                  </ReactMarkdown>
                  {m.streaming && <span className="inline-block w-2 h-4 bg-[#C85A32] align-middle ml-1 animate-pulse" />}
                </div>
              ) : (
                <div className="whitespace-pre-wrap">{m.text}</div>
              )}
              {m.role === "deborah" && m.disclaimer && <Disclaimer>{m.disclaimer}</Disclaimer>}
            </div>
            {m.role === "user" && (
              <div className="w-9 h-9 shrink-0 rounded-full bg-[#1C2024] text-white flex items-center justify-center mt-1">
                <User className="w-5 h-5" />
              </div>
            )}
          </div>
        ))}
        {loading && messages[messages.length - 1]?.streaming === false && (
          <div className="flex items-center gap-2 text-[#4A525A]">
            <Loader2 className="w-4 h-4 animate-spin" /> {t("deborah.thinking")}
          </div>
        )}
      </div>

      <form
        onSubmit={(e) => { e.preventDefault(); send(); }}
        className="mt-3 flex gap-2"
      >
        <input
          data-testid="deborah-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={t("deborah.placeholder")}
          className="flex-1 rounded-xl border-2 border-[#E4DDD3] focus:border-[#C85A32] focus:ring-4 focus:ring-[#C85A32]/20 outline-none p-3 bg-white"
        />
        <MicButton
          testid="deborah-mic"
          onTranscript={(txt) => setInput((prev) => (prev ? `${prev} ${txt}` : txt))}
          language={lang}
          label={t("deborah.mic_label")}
        />
        <button
          type="submit"
          disabled={loading || !input.trim()}
          data-testid="deborah-send"
          className="pod-focus bg-[#C85A32] hover:bg-[#B24C28] disabled:opacity-50 text-white font-bold py-3 px-4 rounded-xl inline-flex items-center gap-2"
        >
          <Send className="w-4 h-4" /> {t("deborah.send")}
        </button>
      </form>
    </div>
  );
}
